import React, { useState, useEffect, useCallback, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Link2, Plus, Edit, Trash2, Search, RefreshCw,
  AlertCircle, X, Cpu, TestTube2, ChevronDown, Loader2
} from 'lucide-react';
import { devicesAPI, labTestsAPI } from '../../services/api';

const AnalyzerMappings = () => {
  const [devices, setDevices] = useState([]);
  const [mappings, setMappings] = useState([]);
  const [labTests, setLabTests] = useState([]);
  const [selectedDevice, setSelectedDevice] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  // Modals
  const [showAddModal, setShowAddModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [selectedMapping, setSelectedMapping] = useState(null);

  // Form state - device_id is now the numeric id
  const [formData, setFormData] = useState({
    device_id: '',  // Numeric id (1, 2, 3...)
    lab_test_id: '',
    online_testcode: '',
    online_testname: ''
  });
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(false);

  // Component-level mapping state
  const [componentsList, setComponentsList] = useState([]);
  const [componentMappings, setComponentMappings] = useState([]);
  const [loadingComponents, setLoadingComponents] = useState(false);

  // Test search
  const [testSearchQuery, setTestSearchQuery] = useState('');
  const [showTestDropdown, setShowTestDropdown] = useState(false);
  const [selectedTest, setSelectedTest] = useState(null);
  const testDropdownRef = useRef(null);

  // Dummy data for testing (with numeric id as per device_full.md)
  const dummyDevices = [
    { _id: '1', id: 1, device_name: 'COBAS_E411', manufacturer: 'Roche', active: true },
    { _id: '2', id: 2, device_name: 'COBAS_C311', manufacturer: 'Roche', active: true },
    { _id: '3', id: 3, device_name: 'BS240', manufacturer: 'Mindray', active: true },
    { _id: '4', id: 4, device_name: 'CL900i', manufacturer: 'Mindray', active: true },
    { _id: '5', id: 5, device_name: 'eCL8000', manufacturer: 'Liftronic', active: true },
  ];


  // Click outside handler
  useEffect(() => {
    function handleClickOutside(event) {
      if (testDropdownRef.current && !testDropdownRef.current.contains(event.target)) {
        setShowTestDropdown(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const fetchDevices = useCallback(async () => {
    try {
      const response = await devicesAPI.getAll();
      const data = response.data?.data || response.data || [];
      setDevices(Array.isArray(data) ? data : dummyDevices);
      if (data.length > 0) {
        setSelectedDevice(data[0]);
      }
    } catch (err) {
      setDevices(dummyDevices);
      setSelectedDevice(dummyDevices[0]);
    }
  }, []);

  const fetchMappings = useCallback(async () => {
    setLoading(true);
    try {
      const response = await devicesAPI.getMappings();
      const data = response.data?.data || response.data || [];
      setMappings(Array.isArray(data) ? data : []);
    } catch (err) {
      setMappings([]);
    }
    setLoading(false);
  }, []);

  const fetchLabTests = useCallback(async () => {
    try {
      const res = await labTestsAPI.getAll({ limit: 500 });
      const items = res.data?.data?.items || res.data?.data || [];
      setLabTests(Array.isArray(items) ? items : []);
    } catch (err) {
      setLabTests([]);
    }
  }, []);

  useEffect(() => {
    fetchDevices();
    fetchMappings();
    fetchLabTests();
  }, [fetchDevices, fetchMappings, fetchLabTests]);

  const resetForm = () => {
    setFormData({ device_id: '', lab_test_id: '', online_testcode: '', online_testname: '' });
    setSelectedTest(null);
    setTestSearchQuery('');
    setComponentsList([]);
    setComponentMappings([]);
  };

  const handleAdd = () => {
    resetForm();
    if (selectedDevice) {
      // Use the numeric id, not _id
      setFormData(prev => ({ ...prev, device_id: selectedDevice.id }));
    }
    setShowAddModal(true);
  };

  const handleEdit = (mapping) => {
    setSelectedMapping(mapping);
    setFormData({
      device_id: mapping.device_id,  // Now a number
      lab_test_id: mapping.lab_test_id?._id || '',
      online_testcode: mapping.online_testcode || '',
      online_testname: mapping.online_testname || ''
    });
    setSelectedTest(mapping.lab_test_id);
    setComponentsList([]);
    setComponentMappings([]);
    setShowEditModal(true);
  };

  const handleLabTestSelect = async (labTestId) => {
    setFormData(prev => ({ ...prev, lab_test_id: labTestId }));
    setLoadingComponents(true);
    try {
      const res = await devicesAPI.getLabTestComponents(labTestId);
      const components = res.data?.data || [];
      setComponentsList(components);
      // Initialize mapping form with empty codes
      setComponentMappings(components.map(c => ({
        test_component_id: c.component_id,
        component_name: c.component_name,
        unit: c.unit,
        section_name: c.section_name,
        online_testcode: '',
        online_testname: ''
      })));
    } catch (err) {
      console.error(err);
      setComponentsList([]);
      setComponentMappings([]);
    }
    setLoadingComponents(false);
  };

  const handleSave = async () => {
    // Edit mode: single mapping update (existing behavior)
    if (showEditModal && selectedMapping) {
      if (!formData.device_id || !formData.lab_test_id || !formData.online_testcode.trim()) {
        setError('جميع الحقول مطلوبة');
        return;
      }
      setSaving(true);
      setError('');
      try {
        await devicesAPI.updateMapping(selectedMapping._id, formData);
        fetchMappings();
        setShowEditModal(false);
        resetForm();
      } catch (err) {
        setError(err.response?.data?.message || 'حدث خطأ أثناء الحفظ');
      }
      setSaving(false);
      return;
    }

    // Add mode: bulk component mappings
    if (!formData.lab_test_id || !formData.device_id) {
      setError('اختر الجهاز والتحليل');
      return;
    }

    // If components were loaded, use bulk save
    if (componentMappings.length > 0) {
      const filledMappings = componentMappings.filter(m => m.online_testcode.trim());
      if (filledMappings.length === 0) {
        setError('أدخل كود الجهاز لعنصر واحد على الأقل');
        return;
      }
      setSaving(true);
      setError('');
      try {
        await devicesAPI.createBulkMappings({
          device_id: formData.device_id,
          lab_test_id: formData.lab_test_id,
          componentMappings: filledMappings
        });
        fetchMappings();
        setShowAddModal(false);
        resetForm();
      } catch (err) {
        setError(err.response?.data?.message || 'فشل الحفظ');
      }
      setSaving(false);
      return;
    }

    // Fallback: no components loaded, use single mapping (old behavior)
    if (!formData.online_testcode.trim()) {
      setError('كود الجهاز مطلوب');
      return;
    }
    setSaving(true);
    setError('');
    try {
      await devicesAPI.createMapping(formData);
      fetchMappings();
      setShowAddModal(false);
      resetForm();
    } catch (err) {
      setError(err.response?.data?.message || 'حدث خطأ أثناء الحفظ');
    }
    setSaving(false);
  };

  const handleDelete = async () => {
    if (!selectedMapping) return;
    setDeleting(true);
    try {
      await devicesAPI.deleteMapping(selectedMapping._id);
      setMappings(prev => prev.filter(m => m._id !== selectedMapping._id));
      setShowDeleteModal(false);
      setSelectedMapping(null);
    } catch (err) {
      setError('حدث خطأ أثناء الحذف');
    }
    setDeleting(false);
  };

  const handleSelectTest = (test) => {
    setSelectedTest(test);
    setTestSearchQuery('');
    setShowTestDropdown(false);

    if (showAddModal) {
      // In add mode, fetch components for this test
      handleLabTestSelect(test._id);
    } else {
      // In edit mode, just set form data normally
      setFormData(prev => ({
        ...prev,
        lab_test_id: test._id,
        online_testname: prev.online_testname || test.name_en || test.name_ar || ''
      }));
    }
  };

  // Filter mappings by selected device (using numeric id)
  const deviceMappings = selectedDevice
    ? mappings.filter((m) => m.device_id === selectedDevice.id)
    : [];
  const orphanMappingsCount = deviceMappings.filter((m) => !m.lab_test_id).length;
  const filteredMappings = deviceMappings.filter((m) => Boolean(m.lab_test_id));

  // Filter tests for search
  const filteredTests = labTests.filter(t =>
    t.name_en?.toLowerCase().includes(testSearchQuery.toLowerCase()) ||
    t.name_ar?.includes(testSearchQuery)
  );

  // Count filled component mappings
  const filledCount = componentMappings.filter(m => m.online_testcode.trim()).length;

  const inputClasses = "w-full px-4 py-2.5 bg-white border border-gray-200 rounded-lg focus:outline-none focus:border-blue-500 focus:ring-1 focus:ring-blue-500";

  return (
    <div className="min-h-screen bg-[#F8FAFC]" dir="rtl">
      <div className="max-w-[1600px] mx-auto px-4 sm:px-6 lg:px-8 py-6 lg:py-8">
      {/* Header */}
      <motion.div
        initial={{ opacity: 0, y: -10 }}
        animate={{ opacity: 1, y: 0 }}
        className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6"
      >
        <div className="flex items-center gap-3">
          <div className="w-12 h-12 rounded-2xl bg-[#1E293B] flex items-center justify-center">
            <Link2 className="w-6 h-6 text-slate-100" strokeWidth={2} />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-[#1E293B] tracking-tight">إدارة ربط الأجهزة بالتحاليل</h1>
            <p className="text-slate-500 text-sm mt-1">Analyzer Test Code Mappings</p>
          </div>
        </div>

        <motion.button
          whileHover={{ scale: 1.02 }}
          whileTap={{ scale: 0.98 }}
          onClick={handleAdd}
          className="inline-flex items-center justify-center gap-2 px-5 py-2.5 bg-blue-600 text-white rounded-xl font-medium text-sm hover:bg-blue-700 transition-all duration-200 shadow-sm"
        >
          <Plus className="w-4 h-4" strokeWidth={2.5} />
          <span>إضافة ربط جديد</span>
        </motion.button>
      </motion.div>

      {/* Error */}
      {error && (
        <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="mb-6 p-4 bg-red-50 border border-red-200 rounded-xl flex items-center gap-3 text-red-700">
          <AlertCircle className="w-5 h-5" />
          <span>{error}</span>
          <button onClick={() => setError('')} className="mr-auto"><X className="w-4 h-4" /></button>
        </motion.div>
      )}

      {/* Device Tabs */}
      <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-4 mb-6">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-lg font-semibold text-gray-800 flex items-center gap-2">
            <Cpu className="w-5 h-5 text-blue-600" />
            أجهزة المختبر
          </h2>
          <button onClick={() => { fetchDevices(); fetchMappings(); }} className="flex items-center gap-2 px-3 py-1.5 text-gray-600 hover:bg-gray-50 rounded-lg text-sm">
            <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
            تحديث
          </button>
        </div>

        <div className="flex flex-wrap gap-2">
          {devices.map((device) => (
            <motion.button
              key={device._id}
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
              onClick={() => setSelectedDevice(device)}
              className={`px-4 py-2 rounded-xl text-sm font-medium transition-all ${
                selectedDevice?.id === device.id
                  ? 'bg-blue-600 text-white shadow-lg shadow-blue-500/30'
                  : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
              }`}
            >
              <span className="font-mono text-xs opacity-70 ml-2">#{device.id}</span>
              {device.device_name}
            </motion.button>
          ))}
        </div>
      </div>

      {/* Mappings Table */}
      <div className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden">
        <div className="px-6 py-4 border-b border-gray-100 bg-gradient-to-l from-blue-50 to-white">
          <h3 className="text-lg font-semibold text-gray-800 flex items-center gap-2">
            <TestTube2 className="w-5 h-5 text-blue-600" />
            التحاليل المرتبطة بـ {selectedDevice?.device_name || '---'}
            {selectedDevice?.id && <span className="text-sm font-mono text-gray-400">(ID: {selectedDevice.id})</span>}
            <span className="text-sm font-normal text-gray-500 mr-2">
              ({filteredMappings.length} تحليل)
            </span>
          </h3>
          {orphanMappingsCount > 0 && (
            <p className="text-xs text-amber-700 mt-1">
              تم إخفاء {orphanMappingsCount} ربط غير صالح (تحليل محذوف/غير موجود). احذفه من الجدول نهائيًا.
            </p>
          )}
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-20">
            <div className="w-12 h-12 border-4 border-blue-200 border-t-blue-600 rounded-full animate-spin" />
          </div>
        ) : filteredMappings.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-20">
            <Link2 className="w-16 h-16 text-gray-300 mb-4" />
            <p className="text-gray-500 mb-4">لا توجد تحاليل مرتبطة بهذا الجهاز</p>
            <button onClick={handleAdd} className="px-5 py-2.5 bg-blue-600 text-white rounded-xl">إضافة ربط</button>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-slate-100">
                  <th className="px-6 py-4 text-right text-[11px] font-semibold text-slate-500 uppercase tracking-wider">#</th>
                  <th className="px-6 py-4 text-right text-[11px] font-semibold text-slate-500 uppercase tracking-wider">اسم التحليل</th>
                  <th className="px-6 py-4 text-right text-[11px] font-semibold text-slate-500 uppercase tracking-wider">العنصر (Component)</th>
                  <th className="px-6 py-4 text-center text-[11px] font-semibold text-slate-500 uppercase tracking-wider">كود الجهاز (online_testcode)</th>
                  <th className="px-6 py-4 text-center text-[11px] font-semibold text-slate-500 uppercase tracking-wider">الإجراءات</th>
                </tr>
              </thead>
              <tbody>
                {filteredMappings.map((mapping, index) => (
                  <motion.tr
                    key={mapping._id}
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    transition={{ delay: index * 0.02 }}
                    className="border-b border-slate-50 hover:bg-slate-50/50 transition-colors duration-150 group"
                  >
                    <td className="px-6 py-5">
                      <span className="text-sm text-slate-500">{index + 1}</span>
                    </td>
                    <td className="px-6 py-5">
                      <div className="flex items-center gap-3">
                        <div className="w-9 h-9 rounded-xl bg-gradient-to-br from-slate-100 to-slate-200 flex items-center justify-center flex-shrink-0">
                          <TestTube2 className="w-4 h-4 text-slate-600" strokeWidth={2} />
                        </div>
                        <div className="min-w-0">
                          <p className="font-semibold text-slate-900 text-sm truncate">{mapping.lab_test_id?.name_en || '-'}</p>
                          <p className="text-xs text-slate-500 mt-0.5 truncate">
                            {mapping.lab_test_id?.name_ar || '-'}
                          </p>
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-5">
                      {mapping.test_component_id ? (
                        <span className="text-sm text-slate-700 font-medium">
                          {mapping.test_component_id?.name || mapping.component_name || '-'}
                        </span>
                      ) : (
                        <span className="text-xs text-slate-400">-</span>
                      )}
                    </td>
                    <td className="px-6 py-5 text-center">
                      <span className="inline-flex items-center px-3 py-1.5 bg-blue-50 text-blue-700 rounded-lg font-mono font-bold text-sm border border-blue-200">
                        {mapping.online_testcode}
                      </span>
                    </td>
                    <td className="px-6 py-5">
                      <div className="flex items-center justify-center gap-1">
                        <button
                          onClick={() => handleEdit(mapping)}
                          className="p-2 text-slate-500 hover:text-slate-900 hover:bg-slate-100 rounded-lg transition-all duration-150"
                          title="تعديل"
                        >
                          <Edit className="w-4 h-4" strokeWidth={2} />
                        </button>
                        <button
                          onClick={() => { setSelectedMapping(mapping); setShowDeleteModal(true); }}
                          className="p-2 text-slate-500 hover:text-rose-600 hover:bg-rose-50 rounded-lg transition-all duration-150"
                          title="حذف"
                        >
                          <Trash2 className="w-4 h-4" strokeWidth={2} />
                        </button>
                      </div>
                    </td>
                  </motion.tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Add Modal (Component-level) */}
      <AnimatePresence>
        {showAddModal && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4"
            onClick={() => { setShowAddModal(false); resetForm(); }}
          >
            <motion.div
              initial={{ scale: 0.9 }}
              animate={{ scale: 1 }}
              exit={{ scale: 0.9 }}
              className="bg-white rounded-2xl w-full max-w-2xl shadow-xl max-h-[90vh] flex flex-col"
              onClick={e => e.stopPropagation()}
            >
              <div className="bg-[#1E293B] px-6 py-4 flex items-center justify-between rounded-t-2xl flex-shrink-0">
                <h3 className="text-lg font-bold text-slate-100 flex items-center gap-2">
                  <Link2 className="w-5 h-5" />
                  إضافة ربط جديد
                </h3>
                <button onClick={() => { setShowAddModal(false); resetForm(); }} className="text-slate-400 hover:text-slate-100 transition-colors">
                  <X className="w-5 h-5" />
                </button>
              </div>

              <div className="p-6 space-y-5 overflow-y-auto flex-1">
                {/* Device Selection - uses numeric id */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    الجهاز <span className="text-red-500">*</span>
                  </label>
                  <select
                    value={formData.device_id}
                    onChange={e => setFormData({...formData, device_id: parseInt(e.target.value, 10)})}
                    className={inputClasses}
                  >
                    <option value="">اختر الجهاز</option>
                    {devices.map(device => (
                      <option key={device._id} value={device.id}>
                        #{device.id} - {device.device_name} {device.manufacturer ? `(${device.manufacturer})` : ''}
                      </option>
                    ))}
                  </select>
                  <p className="text-xs text-gray-400 mt-1">الـ ID الرقمي يُستخدم للتواصل مع الـ Middleware</p>
                </div>

                {/* Test Selection with Search */}
                <div ref={testDropdownRef}>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    التحليل <span className="text-red-500">*</span>
                  </label>
                  <div className="relative">
                    <div
                      onClick={() => setShowTestDropdown(!showTestDropdown)}
                      className={`${inputClasses} cursor-pointer flex items-center justify-between`}
                    >
                      <span className={selectedTest ? 'text-gray-800' : 'text-gray-400'}>
                        {selectedTest ? `${selectedTest.name_en}` : 'اختر التحليل'}
                      </span>
                      <ChevronDown className={`w-4 h-4 text-gray-400 transition-transform ${showTestDropdown ? 'rotate-180' : ''}`} />
                    </div>

                    <AnimatePresence>
                      {showTestDropdown && (
                        <motion.div
                          initial={{ opacity: 0, y: -10 }}
                          animate={{ opacity: 1, y: 0 }}
                          exit={{ opacity: 0, y: -10 }}
                          className="absolute top-full left-0 right-0 mt-1 bg-white border border-gray-200 rounded-xl shadow-xl z-50 max-h-64 overflow-hidden"
                        >
                          <div className="p-2 border-b border-gray-100 sticky top-0 bg-white">
                            <div className="relative">
                              <input
                                type="text"
                                value={testSearchQuery}
                                onChange={e => setTestSearchQuery(e.target.value)}
                                placeholder="ابحث عن التحليل..."
                                className="w-full px-4 py-2 pr-10 bg-gray-50 border border-gray-200 rounded-lg text-sm focus:outline-none focus:border-blue-500"
                                autoFocus
                              />
                              <Search className="absolute right-3 top-2.5 w-4 h-4 text-gray-400" />
                            </div>
                          </div>
                          <div className="overflow-y-auto max-h-48">
                            {filteredTests.length === 0 ? (
                              <div className="p-4 text-center text-gray-500 text-sm">
                                لا توجد نتائج
                              </div>
                            ) : (
                              filteredTests.map(test => (
                                <button
                                  key={test._id}
                                  onClick={() => handleSelectTest(test)}
                                  className={`w-full text-right px-4 py-2.5 hover:bg-gray-50 flex items-center gap-3 transition-colors ${
                                    selectedTest?._id === test._id ? 'bg-blue-50 text-blue-700' : 'text-gray-700'
                                  }`}
                                >
                                  <TestTube2 className="w-4 h-4 text-blue-500" />
                                  <div>
                                    <p className="text-sm font-medium">{test.name_en}</p>
                                    <p className="text-xs text-gray-500">{test.name_ar}</p>
                                  </div>
                                </button>
                              ))
                            )}
                          </div>
                        </motion.div>
                      )}
                    </AnimatePresence>
                  </div>
                </div>

                {/* Loading components spinner */}
                {loadingComponents && (
                  <div className="flex items-center justify-center py-8">
                    <Loader2 className="w-6 h-6 text-blue-500 animate-spin" />
                    <span className="text-sm text-gray-500 mr-2">جاري تحميل عناصر التحليل...</span>
                  </div>
                )}

                {/* Component-level mapping grid */}
                {componentMappings.length > 0 && (
                  <div className="mt-4 border border-gray-200 rounded-xl overflow-hidden">
                    <div className="bg-gray-50 px-4 py-3 border-b border-gray-200">
                      <h4 className="text-sm font-bold text-gray-700">ربط أكواد الجهاز بعناصر التحليل</h4>
                      <p className="text-xs text-gray-500 mt-1">أدخل كود الجهاز لكل عنصر تريد ربطه. اترك الحقل فارغاً للعناصر التي لا تحتاجها.</p>
                    </div>
                    <table className="w-full text-sm">
                      <thead>
                        <tr className="bg-gray-50 text-gray-600 text-xs">
                          <th className="px-4 py-2 text-right font-semibold">العنصر</th>
                          <th className="px-4 py-2 text-right font-semibold">الوحدة</th>
                          <th className="px-4 py-2 text-right font-semibold">كود الجهاز (Test Code)</th>
                        </tr>
                      </thead>
                      <tbody>
                        {componentMappings.map((comp, idx) => (
                          <tr key={comp.test_component_id} className="border-t border-gray-100 hover:bg-gray-50/50">
                            <td className="px-4 py-2.5 font-medium text-gray-800">{comp.component_name}</td>
                            <td className="px-4 py-2.5 text-gray-500">{comp.unit || '-'}</td>
                            <td className="px-4 py-2.5">
                              <input
                                type="text"
                                value={comp.online_testcode}
                                onChange={(e) => {
                                  setComponentMappings(prev => prev.map((c, i) =>
                                    i === idx ? { ...c, online_testcode: e.target.value } : c
                                  ));
                                }}
                                placeholder="مثال: HGB"
                                className="w-full px-3 py-1.5 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-400"
                                dir="ltr"
                              />
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                    {filledCount > 0 && (
                      <div className="px-4 py-2 bg-blue-50 border-t border-blue-100 text-xs text-blue-700">
                        تم تعبئة {filledCount} من {componentMappings.length} عنصر
                      </div>
                    )}
                  </div>
                )}

                {/* Fallback: single testcode input when no components are loaded */}
                {selectedTest && !loadingComponents && componentMappings.length === 0 && (
                  <>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        كود الجهاز (online_testcode) <span className="text-red-500">*</span>
                      </label>
                      <input
                        type="text"
                        value={formData.online_testcode}
                        onChange={e => setFormData({...formData, online_testcode: e.target.value})}
                        className={inputClasses}
                        placeholder="مثال: GLU, CRE, TSH"
                        dir="ltr"
                      />
                      <p className="text-xs text-gray-400 mt-1">الكود الذي يستخدمه الجهاز/الـ Middleware للتعرف على هذا التحليل</p>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        اسم التحليل لدى الجهاز (online_testname)
                      </label>
                      <input
                        type="text"
                        value={formData.online_testname}
                        onChange={e => setFormData({...formData, online_testname: e.target.value})}
                        className={inputClasses}
                        placeholder="مثال: Hemoglobin / WBC"
                        dir="ltr"
                      />
                      <p className="text-xs text-gray-400 mt-1">اختياري — يرسل كاسم التحليل في DeviceTestOrderRequests</p>
                    </div>
                  </>
                )}

                {error && (
                  <div className="p-3 bg-red-50 text-red-600 rounded-lg text-sm flex items-center gap-2">
                    <AlertCircle className="w-4 h-4" />
                    {error}
                  </div>
                )}
              </div>

              <div className="px-6 py-4 border-t border-gray-100 flex justify-end gap-3 flex-shrink-0">
                <button
                  onClick={() => { setShowAddModal(false); resetForm(); }}
                  className="px-6 py-2.5 text-gray-600 hover:bg-gray-100 rounded-xl font-medium"
                >
                  إلغاء
                </button>
                <button
                  onClick={handleSave}
                  disabled={saving}
                  className="px-6 py-2.5 bg-blue-600 text-white rounded-xl font-medium hover:bg-blue-700 disabled:opacity-50 flex items-center gap-2"
                >
                  {saving ? (
                    <><div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />جاري الحفظ...</>
                  ) : (
                    componentMappings.length > 0 ? `ربط ${filledCount} عنصر` : 'إضافة الربط'
                  )}
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Edit Modal (single mapping) */}
      <AnimatePresence>
        {showEditModal && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4"
            onClick={() => { setShowEditModal(false); resetForm(); }}
          >
            <motion.div
              initial={{ scale: 0.9 }}
              animate={{ scale: 1 }}
              exit={{ scale: 0.9 }}
              className="bg-white rounded-2xl w-full max-w-lg shadow-xl"
              onClick={e => e.stopPropagation()}
            >
              <div className="bg-[#1E293B] px-6 py-4 flex items-center justify-between rounded-t-2xl">
                <h3 className="text-lg font-bold text-slate-100 flex items-center gap-2">
                  <Link2 className="w-5 h-5" />
                  تعديل الربط
                </h3>
                <button onClick={() => { setShowEditModal(false); resetForm(); }} className="text-slate-400 hover:text-slate-100 transition-colors">
                  <X className="w-5 h-5" />
                </button>
              </div>

              <div className="p-6 space-y-5">
                {/* Device Selection - uses numeric id */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    الجهاز <span className="text-red-500">*</span>
                  </label>
                  <select
                    value={formData.device_id}
                    onChange={e => setFormData({...formData, device_id: parseInt(e.target.value, 10)})}
                    className={inputClasses}
                  >
                    <option value="">اختر الجهاز</option>
                    {devices.map(device => (
                      <option key={device._id} value={device.id}>
                        #{device.id} - {device.device_name} {device.manufacturer ? `(${device.manufacturer})` : ''}
                      </option>
                    ))}
                  </select>
                  <p className="text-xs text-gray-400 mt-1">الـ ID الرقمي يُستخدم للتواصل مع الـ Middleware</p>
                </div>

                {/* Test Selection with Search */}
                <div ref={testDropdownRef}>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    التحليل <span className="text-red-500">*</span>
                  </label>
                  <div className="relative">
                    <div
                      onClick={() => setShowTestDropdown(!showTestDropdown)}
                      className={`${inputClasses} cursor-pointer flex items-center justify-between`}
                    >
                      <span className={selectedTest ? 'text-gray-800' : 'text-gray-400'}>
                        {selectedTest ? `${selectedTest.name_en}` : 'اختر التحليل'}
                      </span>
                      <ChevronDown className={`w-4 h-4 text-gray-400 transition-transform ${showTestDropdown ? 'rotate-180' : ''}`} />
                    </div>

                    <AnimatePresence>
                      {showTestDropdown && (
                        <motion.div
                          initial={{ opacity: 0, y: -10 }}
                          animate={{ opacity: 1, y: 0 }}
                          exit={{ opacity: 0, y: -10 }}
                          className="absolute top-full left-0 right-0 mt-1 bg-white border border-gray-200 rounded-xl shadow-xl z-50 max-h-64 overflow-hidden"
                        >
                          <div className="p-2 border-b border-gray-100 sticky top-0 bg-white">
                            <div className="relative">
                              <input
                                type="text"
                                value={testSearchQuery}
                                onChange={e => setTestSearchQuery(e.target.value)}
                                placeholder="ابحث عن التحليل..."
                                className="w-full px-4 py-2 pr-10 bg-gray-50 border border-gray-200 rounded-lg text-sm focus:outline-none focus:border-blue-500"
                                autoFocus
                              />
                              <Search className="absolute right-3 top-2.5 w-4 h-4 text-gray-400" />
                            </div>
                          </div>
                          <div className="overflow-y-auto max-h-48">
                            {filteredTests.length === 0 ? (
                              <div className="p-4 text-center text-gray-500 text-sm">
                                لا توجد نتائج
                              </div>
                            ) : (
                              filteredTests.map(test => (
                                <button
                                  key={test._id}
                                  onClick={() => handleSelectTest(test)}
                                  className={`w-full text-right px-4 py-2.5 hover:bg-gray-50 flex items-center gap-3 transition-colors ${
                                    selectedTest?._id === test._id ? 'bg-blue-50 text-blue-700' : 'text-gray-700'
                                  }`}
                                >
                                  <TestTube2 className="w-4 h-4 text-blue-500" />
                                  <div>
                                    <p className="text-sm font-medium">{test.name_en}</p>
                                    <p className="text-xs text-gray-500">{test.name_ar}</p>
                                  </div>
                                </button>
                              ))
                            )}
                          </div>
                        </motion.div>
                      )}
                    </AnimatePresence>
                  </div>
                </div>

                {/* Analyzer Code (online_testcode) */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    كود الجهاز (online_testcode) <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    value={formData.online_testcode}
                    onChange={e => setFormData({...formData, online_testcode: e.target.value})}
                    className={inputClasses}
                    placeholder="مثال: GLU, CRE, TSH"
                    dir="ltr"
                  />
                  <p className="text-xs text-gray-400 mt-1">الكود الذي يستخدمه الجهاز/الـ Middleware للتعرف على هذا التحليل</p>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    اسم التحليل لدى الجهاز (online_testname)
                  </label>
                  <input
                    type="text"
                    value={formData.online_testname}
                    onChange={e => setFormData({...formData, online_testname: e.target.value})}
                    className={inputClasses}
                    placeholder="مثال: Hemoglobin / WBC"
                    dir="ltr"
                  />
                  <p className="text-xs text-gray-400 mt-1">اختياري — يرسل كاسم التحليل في DeviceTestOrderRequests</p>
                </div>

                {error && (
                  <div className="p-3 bg-red-50 text-red-600 rounded-lg text-sm flex items-center gap-2">
                    <AlertCircle className="w-4 h-4" />
                    {error}
                  </div>
                )}
              </div>

              <div className="px-6 py-4 border-t border-gray-100 flex justify-end gap-3">
                <button
                  onClick={() => { setShowEditModal(false); resetForm(); }}
                  className="px-6 py-2.5 text-gray-600 hover:bg-gray-100 rounded-xl font-medium"
                >
                  إلغاء
                </button>
                <button
                  onClick={handleSave}
                  disabled={saving}
                  className="px-6 py-2.5 bg-blue-600 text-white rounded-xl font-medium hover:bg-blue-700 disabled:opacity-50 flex items-center gap-2"
                >
                  {saving ? (
                    <><div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />جاري الحفظ...</>
                  ) : (
                    'حفظ التغييرات'
                  )}
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Delete Modal */}
      <AnimatePresence>
        {showDeleteModal && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4"
            onClick={() => setShowDeleteModal(false)}
          >
            <motion.div
              initial={{ scale: 0.9 }}
              animate={{ scale: 1 }}
              exit={{ scale: 0.9 }}
              className="bg-white rounded-2xl p-6 w-full max-w-md shadow-xl"
              onClick={e => e.stopPropagation()}
            >
              <div className="text-center">
                <div className="w-16 h-16 rounded-full bg-red-100 flex items-center justify-center mx-auto mb-4">
                  <Trash2 className="w-8 h-8 text-red-600" />
                </div>
                <h3 className="text-xl font-bold text-gray-800 mb-2">حذف الربط</h3>
                <p className="text-gray-500 mb-6">
                  هل أنت متأكد من حذف ربط التحليل "{selectedMapping?.lab_test_id?.name_en}"
                  (الكود: {selectedMapping?.online_testcode})؟
                </p>
                <div className="flex gap-3">
                  <button onClick={() => setShowDeleteModal(false)} className="flex-1 px-4 py-2.5 text-gray-600 hover:bg-gray-100 rounded-xl font-medium">إلغاء</button>
                  <button onClick={handleDelete} disabled={deleting} className="flex-1 px-4 py-2.5 bg-red-600 text-white rounded-xl font-medium hover:bg-red-700 disabled:opacity-50 flex items-center justify-center gap-2">
                    {deleting ? <><div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />جاري الحذف...</> : 'حذف'}
                  </button>
                </div>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
      </div>
    </div>
  );
};

export default AnalyzerMappings;
